<template>
  <div class="explore-page">
    <div class="explore-inner">
      <div class="explore-layout">
        <!-- Left Sidebar -->
        <div class="sidebar" v-loading="categoryLoading">
          <div
            v-for="(cat, index) in categories"
            :key="cat.categoryId || index"
            :class="['sidebar-item', { active: activeCategoryIndex === index }]"
            @click="onCategoryChange(index)"
            :title="cat.categoryName"
          >
            <span class="dot"></span>
            <span class="sidebar-text">{{ translateCategory(cat.categoryName) }}</span>
          </div>
          <div v-if="!categoryLoading && !categories.length" class="empty-tip">
            {{ $t("common.empty") }}
          </div>
        </div>

        <!-- Right Content -->
        <div class="content">
          <h1 class="page-title">{{ translateCategory(currentCategoryName) }}</h1>

          <!-- Tabs (pill style) - second-level categories -->
          <div v-if="secondCategories.length" class="tab-bar">
            <span
              v-for="sub in secondCategories"
              :key="sub.categoryId"
              :class="['tab-item', { active: activeSecondId === sub.categoryId }]"
              @click="onSecondCategoryChange(sub.categoryId)"
            >
              {{ translateCategory(sub.categoryName) }}
            </span>
          </div>

          <!-- Sort -->
          <div class="sort-bar">
            <span class="sort-label">{{ $t("explore.sortBy") }}</span>
            <span class="sort-option" @click="onSortToggle">
              {{ $t("explore.sortPublishDate") }}
              <i :class="orderType === 1 ? 'el-icon-top' : 'el-icon-bottom'"></i>
            </span>
          </div>

          <!-- Book Grid / Empty -->
          <div class="book-results" v-loading="bookLoading">
            <div v-if="bookList.length" class="book-grid">
              <div v-for="book in bookList" :key="book.id">
                <book-card :book="book" @click="goToBookDetail(book)" />
              </div>
            </div>
            <el-empty
              v-else-if="!bookLoading"
              :description="$t('common.empty')"
            ></el-empty>
          </div>

          <!-- Pagination -->
          <div v-if="total > pageSize" class="pagination-wrapper">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page.sync="currentPage"
              :prev-text="$t('explore.prevPage')"
              :next-text="$t('explore.nextPage')"
              @current-change="handlePageChange"
            ></el-pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BookCard from "@/components/BookCard.vue";
import { getImage, formatAuthorName } from "@/utils/tools";
import { getCategoryList } from "@/api/home";
import { listBookPage } from "@/api/book";

export default {
  name: "ExplorePage",
  components: { BookCard },
  data() {
    return {
      activeCategoryIndex: 0,
      activeSecondId: null,
      orderType: 2,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      categories: [],
      bookList: [],
      categoryLoading: false,
      bookLoading: false,
    };
  },
  watch: {
    '$i18n.locale'() {
      this.bookList.forEach((book) => {
        book.author = formatAuthorName(book.authorList, book.authorName);
      });
    },
  },
  computed: {
    currentCategory() {
      return this.categories[this.activeCategoryIndex] || null;
    },
    currentCategoryName() {
      return this.currentCategory ? this.currentCategory.categoryName : "";
    },
    secondCategories() {
      return this.currentCategory &&
        Array.isArray(this.currentCategory.secondCategoryList)
        ? this.currentCategory.secondCategoryList
        : [];
    },
  },
  mounted() {
    this.loadCategories();
  },
  methods: {
    translateCategory(name) {
      if (!name) return ''
      const key = `categoryNames.${name}`
      const translated = this.$i18n.t(key)
      return translated === key ? name : translated
    },
    async loadCategories() {
      this.categoryLoading = true;
      try {
        const res = await getCategoryList();
        const list = Array.isArray(res?.data) ? res.data : res?.rows || [];
        this.categories = list;
        if (list.length) {
          this.activeCategoryIndex = 0;
          this.activeSecondId = this.firstSecondId(list[0]);
          this.loadBookList();
        } else {
          this.activeSecondId = null;
          this.bookList = [];
          this.total = 0;
        }
      } catch (err) {
        this.categories = [];
        this.activeSecondId = null;
      } finally {
        this.categoryLoading = false;
      }
    },
    firstSecondId(cat) {
      const list = cat && Array.isArray(cat.secondCategoryList)
        ? cat.secondCategoryList
        : [];
      return list.length ? list[0].categoryId : null;
    },
    async loadBookList() {
      const cat = this.currentCategory;
      if (!cat) {
        this.bookList = [];
        this.total = 0;
        return;
      }
      this.bookLoading = true;
      try {
        const payload = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          categoryIdFirst: cat.categoryId,
          orderField: 1,
          orderType: this.orderType,
          searchValue: "",
        };
        if (this.activeSecondId != null) {
          payload.categoryIdSecond = this.activeSecondId;
        }
        const res = await listBookPage(payload);
        const rows = Array.isArray(res?.rows) ? res.rows : [];
        this.bookList = rows.map((item) => ({
          id: item.bookId,
          title: item.bookName,
          titleEn: item.bookNameEn,
          authorList: item.authorList,
          authorName: item.authorName,
          author: formatAuthorName(item.authorList, item.authorName),
          date: item.publicationDate,
          cover: item.bookImageAddr ? getImage(item.bookImageAddr) : null,
        }));
        this.total = Number(res?.total) || 0;
      } catch (err) {
        this.bookList = [];
        this.total = 0;
      } finally {
        this.bookLoading = false;
      }
    },
    onCategoryChange(index) {
      if (this.activeCategoryIndex === index) return;
      this.activeCategoryIndex = index;
      this.activeSecondId = this.firstSecondId(this.categories[index]);
      this.currentPage = 1;
      this.loadBookList();
    },
    onSecondCategoryChange(id) {
      if (this.activeSecondId === id) return;
      this.activeSecondId = id;
      this.currentPage = 1;
      this.loadBookList();
    },
    onSortToggle() {
      this.orderType = this.orderType === 2 ? 1 : 2;
      this.currentPage = 1;
      this.loadBookList();
    },
    handlePageChange() {
      this.loadBookList();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    goToBookDetail(book) {
      this.$router.push({ path: `/book/${book.id}` });
    },
  },
};
</script>

<style lang="scss" scoped>
.explore-page {
  flex: 1;
  padding: 20px 0 40px;
}

.explore-inner {
  max-width: $max-width;
  margin: 0 auto;
}

.explore-layout {
  display: flex;
  gap: 20px;
}

/* ===== Sidebar ===== */
.sidebar {
  flex-shrink: 0;
  width: 260px;
  padding: 20px 0;

  .sidebar-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    font-size: 18px;
    color: $text-dark;
    cursor: pointer;
    margin-bottom: 2px;
    transition: all 0.2s;

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: transparent;
      flex-shrink: 0;
      transition: background 0.2s;
    }

    .sidebar-text {
      line-height: 1.5;
    }

    &:hover {
      color: $primary-orange;
    }

    &.active {
      color: $primary-orange;

      .dot {
        background: $primary-orange;
      }
    }
  }
}

.empty-tip {
  color: $text-gray;
  font-size: 14px;
  padding: 12px;
}

/* ===== Content ===== */
.content {
  flex: 1;
  min-width: 0;

  .page-title {
    font-size: 28px;
    color: $title-color;
    margin-bottom: 16px;
  }
}

/* ===== Tabs (pill / rounded style) ===== */
.tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;

  .tab-item {
    padding: 8px 14px;
    font-size: 16px;
    border-radius: 16px;
    border: 1px solid $border-gray;
    color: $text-gray;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: $primary-orange;
      color: $text-gray;
    }

    &.active {
      background: $bg-orange-light;
      border-color: $primary-orange;
      color: $text-dark;
    }
  }
}

.sort-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 16px;
  color: $text-gray;

  .sort-label {
    font-size: 16px;
  }

  .sort-option {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 16px;
    color: $primary-orange;
    cursor: pointer;
    user-select: none;

    i {
      font-weight: 700;
      font-size: 16px;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .result-range {
    margin-left: auto;
    color: $text-light;
  }
}

/* ===== Book Grid: 4 columns ===== */
.book-results {
  min-height: 200px;
  margin-top: 30px;
}

.book-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
}

/* ===== Pagination (orange theme) ===== */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 24px;

  ::v-deep .el-pagination.is-background {
    .btn-prev,
    .btn-next {
      background: transparent;
      color: $text-gray;
      font-size: 13px;
      padding: 0 8px;

      &:hover {
        color: $primary-orange;
      }

      &:disabled {
        color: #c0c4cc;
      }
    }

    .el-pager li {
      background: transparent;
      color: $text-gray;
      font-weight: 400;
      min-width: 28px;

      &:hover {
        color: $primary-orange;
      }

      &.is-active {
        background-color: $primary-orange;
        color: $white;
        border-radius: 4px;
      }
    }
  }
}
</style>
